<script setup lang="ts">
import { Carousel, Slide, Pagination, Navigation } from 'vue3-carousel'
import { useI18n } from 'vue-i18n';
import  messages  from "@/locales/homepage/messages";
import { ref, computed } from 'vue';
import { subscribeNewsletter } from '@/api/user';
import { useToast, POSITION } from 'vue-toastification';
import BlackFriday from '@/components/BlackFriday.vue';
import ChristmasPromo from '@/components/ChristmasPromo.vue';
import SalonPhoto2026 from '@/components/SalonPhoto2026/index.vue';
import { useSalonPhoto2026Active } from '@/components/SalonPhoto2026/period';

const { t, locale } = useI18n({
    useScope: 'global',
    messages
})

const toast = useToast();
const email_newsletter = ref('')

const salonPhoto2026Active = useSalonPhoto2026Active()
function first_word(str: string){
    return str.split(' ')[0]
}
function rest_of_string(str: string){
    return str.split(' ').slice(1).join(' ')
}
const carouselConfig = {
    itemsToShow: 1,
    wrapAround: true,
    pauseAutoplayOnHover: true,
    autoplay: 7500,
    transition: 700
}
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const spinnerNewsletter = ref(false)
function doSubscribeNewsletter(){
    // we check that the email is valid
    if(!email_newsletter.value.match(emailRegex)){
        toast.error(t('home.newsletter.invalid'))
        return
    }
    spinnerNewsletter.value = true
    subscribeNewsletter(email_newsletter.value,locale.value).then((res)=>{
        if(res.result==1){
            toast.success(t('home.newsletter.success'))
        }
        else if(res.result==2){
            toast.error(t('home.newsletter.success2'))
        }
        email_newsletter.value = ''
    }).catch((err)=>{
        toast.error(t('home.newsletter.error'))
    }).finally(()=>{
        spinnerNewsletter.value = false
    })
}
</script>
<template>
    <BlackFriday variant="banner" />
    <ChristmasPromo variant="banner" />
    <!-- Pendant le Salon de la Photo 2026, la slide promo remplace le carrousel -->
    <SalonPhoto2026 v-if="salonPhoto2026Active" variant="slide" />
    <Carousel v-else v-bind="carouselConfig">
        <Slide>
            <div class="homeslide" id="slide1">
                <h2>{{$t('home.slides.slide1.title')}}</h2>
            </div>
        </Slide>
        <Slide>
            <div class="homeslide" id="slide2">
                <h2>{{$t('home.slides.slide2.title')}}</h2>
                <z-button color="primary" :to="'/'+locale+'/category/circular/magnetic'">{{$t('home.slides.slide2.cta')}}</z-button>
            </div>
        </Slide>
        <Slide>
            <div class="homeslide" id="slide3">
                <h2>{{$t('home.slides.slide3.title')}}</h2>
                <z-button color="primary" :to="'/'+locale+'/category/square/100mm_armour_magnetic'">{{$t('home.slides.slide3.cta')}}</z-button>
            </div>
        </Slide>
        <Slide>
            <div class="homeslide" id="slide4">
                <h2>{{$t('home.slides.slide4.title')}}</h2>
                <z-button color="primary" :to="'/'+locale+'/category/circular/screw'">{{$t('home.slides.slide4.cta')}}</z-button>
            </div>
        </Slide>
        <Slide>
            <div class="homeslide" id="slide5">
                <h2>{{$t('home.slides.slide5.title')}}</h2>
                <z-button color="primary" :to="'/'+locale+'/category/lenses'">{{$t('home.slides.slide5.cta')}}</z-button>
            </div>
        </Slide>

        <template #addons>
            <Navigation />
        </template>
    </Carousel>
    <div class="products-families">
        <router-link v-for="num in 3" :key="num" :to="$t('home.families.family'+num+'.to')" class="product-family">
            <img :src="$t('home.families.family'+num+'.img')" :alt="$t('home.families.family'+num+'.title')" />
            <h3><span>{{ first_word($t('home.families.family'+num+'.title')) }}</span> {{rest_of_string($t('home.families.family'+num+'.title'))}}</h3>
        </router-link>
    </div>
    <v-form id="subscribe_newsletter" @submit.prevent="doSubscribeNewsletter">
        <div v-if="spinnerNewsletter" class="position-absolute top-0 left-0 w-100 h-100 d-flex justify-center align-center bg-white opacity-50">
            <v-progress-circular indeterminate></v-progress-circular>
        </div>
        <h2>{{ $t('home.newsletter.title')}}</h2>
        <h4>{{ $t('home.newsletter.subtitle')}}</h4>
        <input type="email" v-model="email_newsletter" :placeholder="$t('home.newsletter.placeholder')" />
        <input type="submit" :value="$t('home.newsletter.cta')" />
    </v-form>
</template>
<style>
:root {
    --vc-clr-primary: #FFF;
    --vc-clr-secondary: #DC0303;
    --vc-nav-height: 50px;
    --vc-nav-width: 50px;
}
</style>
<style lang="scss" scoped>
#subscribe_newsletter{
    position: relative;
    margin: 1rem auto;
    width: calc(100vw - 2rem);
    border: 5px solid #000000;
    box-shadow: 0px 4px 8px 0px #00000040;
    padding: 1rem;
    h2{
        font-family: Montserrat;
        font-size: 24px;
        font-weight: 700;
        line-height: 29.26px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        text-transform: uppercase;
    }
    h4{
        font-family: Montserrat;
        font-size: 12px;
        font-weight: 700;
        line-height: 14.63px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        text-transform: uppercase;
    }
    input[type="email"]{
        width: 100%;
        margin: 1rem 0;
        border-bottom: 1px solid #B4B4B4
    }
    input[type="submit"]{
        width: 100%;
        background-color: #DC0303;
        color: #FFF;
        font-family: Montserrat;
        font-size: 14px;
        font-weight: 400;
        line-height: 17.07px;
        text-align: center;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        line-height: 31px;
        border: none;
        border-radius: 20px;
        cursor: pointer;
    }

}
.products-families{
    display: block;
    width: 100%;
    padding: 0 1rem 1rem 1rem;
    .product-family{
        display: block;
        margin-top: 1rem;
        box-shadow: 0px 0px 8px 0px #00000040;
        height: 75px;
        position: relative;
        transition: all .3s;
        img{
            position: absolute;
            right: 0;
            height: 100%;
            object-fit: cover;
        }
        h3{
            position: absolute;
            left: 0;
            margin: 0 1rem;
            line-height: 75px;
            font-family: Montserrat;
            font-size: 24px;
            font-weight: 700;
            text-align: left;
            text-underline-position: from-font;
            text-decoration-skip-ink: none;
            color: #000;
        }
        &:nth-child(even){
            img{
                left: 0;
                right: auto;
            }
            h3{
                right: 0;
                left: auto;
            }
        }
        &:hover{
            box-shadow: 0px 0px 15px 0px #000000CC;
        }
    }
}

.homeslide{
    width: 100%;
    height: 50vh;
    background-size: cover;
    background-position: top center;
    text-align: center;
    h2{
        color: white;
        font-weight: 700;
        font-size: 2.5rem;
        text-align: center;
        margin-top: 3rem;
        text-shadow: 1px 1px 15px #00000040;
        padding: 0 1rem;

    }
}
#slide1{
    background-image: url('@/assets/carousel_home/slider1.jpg');
}
#slide2{
    background-image: url('@/assets/carousel_home/slider2.jpg');
}
#slide3{
    background-image: url('@/assets/carousel_home/slider3.jpg');
}
#slide4{
    background-image: url('@/assets/carousel_home/slider4.jpg');
}
#slide5{
    background-image: url('@/assets/carousel_home/slider5.jpg');
}
@media only screen and (min-width : $responsive-point) {
    #subscribe_newsletter{
        width: 874px;
        padding: 1rem 13rem;
        h2, h4{
            text-align-last: justify;
        }
    }
    .products-families{
        display: flex;
        flex-wrap: wrap;
        position: relative;
        width: 874px;
        margin: -100px auto 40px auto;
        z-index: 10;
        padding: 0;
        background-color: #FFF;
        box-shadow: 0px 0px 8px 0px #00000040;
        .product-family{
            display: inline-block;
            width: calc(100% / 3);
            box-shadow: none;
            // background gradient white to grey left to right
            margin: 0;

            background: linear-gradient(70deg, #FFF 0%, #F4F4F4 100%);
            transition: all .3s;
            
            &:hover{
                box-shadow: none;
                background: linear-gradient(120deg, #CCC 0%,#F1F1F1  100%);
            }
            height: 200px;
            img{
                max-height: 100%;
                max-width: 70%;
                object-fit: contain;
                object-position: right bottom;
            }
            h3{
                text-align: left;
                line-height: 1.1;
                font-size: 27px;
                margin: 1rem;
                span{
                    display: block;
                    // we display the word in white with black border
                    color: #FFF;
                    text-shadow: 0px 0px 2px #000;
                }
            }
            &:nth-child(even){
                img{
                    object-position: right top;
                    left: auto;
                    right: 0;
                }
                h3{
                    right: auto;
                    left: 0;
                    bottom: 0;
                    text-align: left;
                }
            }
        }
    }
}
</style>
<route lang="yaml">
name: 'home'
props: true
meta:
    title: 'KaseFilter Home'
    layout: 'default'
    middleware: 'guest'
</route>